<template>
  <div class="plan-completion-page nx-page nx-report-page">
    <nx-report-header title="计划完成率分析">
      <div class="head-actions">
        <el-button type="primary" plain icon="el-icon-download" @click="exportReport">导出报表</el-button>
        <el-button icon="el-icon-setting" @click="settingsVisible = true">设置</el-button>
      </div>
    </nx-report-header>

    <el-card shadow="never" class="filter-card">
      <div class="filter-title">筛选条件</div>
      <el-form inline :model="query" class="filter-form">
        <el-form-item label="时间范围">
          <el-select v-model="timePreset" style="width: 120px" @change="onPresetChange">
            <el-option label="本月" value="month" />
            <el-option label="上月" value="lastMonth" />
            <el-option label="近30天" value="d30" />
            <el-option label="自定义" value="custom" />
          </el-select>
          <el-date-picker
            v-if="timePreset === 'custom'"
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始"
            end-placeholder="结束"
            value-format="yyyy-MM-dd"
            unlink-panels
            :picker-options="pickerOptions"
            style="margin-left: 8px; width: 260px"
          />
        </el-form-item>
        <el-form-item label="客户">
          <el-select
            v-model="query.custNo"
            filterable
            remote
            reserve-keyword
            clearable
            placeholder="检索客户"
            :remote-method="remoteCustOwner"
            :loading="cargoOwnerLoading"
            style="min-width: 200px"
          >
            <el-option
              v-for="c in cargoOwnerList"
              :key="'co-' + c.id"
              :label="c.deptName || ''"
              :value="String(c.id)"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="煤种">
          <el-select
            v-model="query.cargoDictId"
            filterable
            remote
            reserve-keyword
            clearable
            placeholder="检索"
            :remote-method="remoteCargo"
            :loading="cargoLoading"
            style="min-width: 200px"
            @change="onCargoChange"
          >
            <el-option
              v-if="selectedCargo && !cargoList.some((c) => sameId(c.id, selectedCargo.id))"
              :key="'sel-' + selectedCargo.id"
              :label="cargoLabel(selectedCargo)"
              :value="selectedCargo.id"
            />
            <el-option
              v-for="c in cargoList"
              :key="c.id"
              :label="cargoLabel(c)"
              :value="c.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="计划状态">
          <el-radio-group v-model="query.planStatus">
            <el-radio-button label="">全部</el-radio-button>
            <el-radio-button label="completed">已完成</el-radio-button>
            <el-radio-button label="in_progress">进行中</el-radio-button>
            <el-radio-button label="overdue">超期</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="applyFilter">应用筛选</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-row :gutter="16" class="kpi-row">
      <el-col :xs="24" :sm="12" :md="6">
        <el-card shadow="hover" class="kpi-card kpi-blue">
          <div class="kpi-icon"><i class="el-icon-success" /></div>
          <div class="kpi-val">{{ fmtPercent(kpi.overallAchievementRate) }}</div>
          <div class="kpi-label">整体达成率</div>
          <div class="kpi-sub">月度计划完成度</div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <el-card shadow="hover" class="kpi-card kpi-red">
          <div class="kpi-icon"><i class="el-icon-warning" /></div>
          <div class="kpi-val">{{ kpi.overduePlanCount != null ? kpi.overduePlanCount : 0 }}</div>
          <div class="kpi-label">超期计划数</div>
          <div class="kpi-sub">需重点关注</div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <el-card shadow="hover" class="kpi-card kpi-gold">
          <div class="kpi-icon"><i class="el-icon-data-line" /></div>
          <div class="kpi-val">{{ fmtSignedPercent(kpi.avgDeviationRatePercent) }}</div>
          <div class="kpi-label">平均偏差率</div>
          <div class="kpi-sub">实际相对计划（按吨）</div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <el-card shadow="hover" class="kpi-card kpi-green">
          <div class="kpi-icon"><i class="el-icon-tickets" /></div>
          <div class="kpi-val">{{ kpi.continuousComplianceWeeks != null ? kpi.continuousComplianceWeeks : 0 }} 周</div>
          <div class="kpi-label">连续达标</div>
          <div class="kpi-sub">周度达成率≥目标</div>
        </el-card>
      </el-col>
    </el-row>

    <el-card shadow="hover" class="block-card">
      <div class="card-head">
        <span class="card-title">计划 vs 实际对比</span>
        <el-radio-group v-model="sumType" size="small" @change="loadCharts">
          <el-radio-button label="1">按周</el-radio-button>
          <el-radio-button label="2">按月</el-radio-button>
        </el-radio-group>
      </div>
      <div ref="dualChart" class="echart-box tall" />
    </el-card>

    <el-card shadow="hover" class="block-card">
      <div class="card-head">
        <span class="card-title">达成率趋势分析</span>
        <div class="target-hint">
          目标达成率: {{ targetRate }}%
          <el-button type="text" icon="el-icon-setting" @click="settingsVisible = true">调整目标</el-button>
        </div>
      </div>
      <div ref="trendChart" class="echart-box" />
    </el-card>

    <el-row :gutter="16" class="bottom-row">
      <el-col :xs="24" :lg="24">
        <el-card shadow="hover" class="block-card">
          <div class="card-head">
            <span class="card-title">偏差原因分析</span>
          </div>
          <div ref="pieChart" class="echart-box pie-box" />
          <div class="reason-foot">
            <div
              v-for="(it, idx) in deviationTop"
              :key="it.reasonName || idx"
              class="reason-item"
              :class="'c' + (idx % 4)"
            >
              <div class="reason-name">{{ it.reasonName }}</div>
              <div class="reason-meta">
                占比 {{ it.ratioPercent != null ? it.ratioPercent : 0 }}%
                <span v-if="it.momDeltaPercent != null" class="mom">
                  ，较上月 {{ momText(it.momDeltaPercent) }}
                </span>
              </div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-card shadow="hover" class="block-card table-card">
      <div class="card-head">
        <span class="card-title">计划执行明细</span>
        <div>
          <el-input
            v-model="tableKeyword"
            clearable
            maxlength="50"
            placeholder="搜索计划编号/内容/车次"
            style="width: 220px; margin-right: 8px"
            @keyup.enter.native="reloadTable"
          />
          <el-button type="primary" size="small" icon="el-icon-search" @click="reloadTable">查询</el-button>
        </div>
      </div>
      <el-table v-loading="tableLoading" :data="tableRecords" border stripe style="width: 100%">
        <el-table-column prop="planLineNo" label="计划编号" min-width="140" show-overflow-tooltip />
        <el-table-column prop="planDate" label="计划日期" width="120" />
        <el-table-column prop="planContent" label="计划内容" min-width="160" show-overflow-tooltip />
        <el-table-column prop="custName" label="客户" min-width="120" show-overflow-tooltip />
        <el-table-column prop="planQuantity" label="计划量(吨)" width="110" align="right" :formatter="fmtTon" />
        <el-table-column prop="actualQuantity" label="实际完成量(吨)" width="130" align="right" :formatter="fmtTon" />
        <el-table-column label="达成率" width="160">
          <template slot-scope="{ row }">
            <el-progress
              :percentage="progressPct(row.achievementRate)"
              :color="rateColor(row.achievementRate)"
              :format="() => fmtPercent(row.achievementRate)"
            />
          </template>
        </el-table-column>
        <el-table-column prop="deviationQty" label="偏差量(吨)" width="110" align="right">
          <template slot-scope="{ row }">
            <span :class="deviationClass(row.deviationQty)">{{ fmtTonCell(row.deviationQty) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="deviationReason" label="偏差原因" min-width="120" show-overflow-tooltip />
        <el-table-column prop="planStatusLabel" label="状态" width="90">
          <template slot-scope="{ row }">
            <el-tag :type="statusTag(row.planStatus)" size="small">{{ row.planStatusLabel }}</el-tag>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        class="pager"
        background
        layout="total, prev, pager, next"
        :total="tableTotal"
        :page-size="pagination.size"
        :current-page.sync="pagination.page"
        @current-change="loadTable"
      />
    </el-card>

    <el-dialog title="分析设置" :visible.sync="settingsVisible" width="400px" append-to-body>
      <el-form label-width="120px">
        <el-form-item label="目标达成率(%)">
          <el-input-number v-model="targetRate" :min="60" :max="100" :step="1" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="settingsVisible = false">取消</el-button>
        <el-button type="primary" @click="saveSettings">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'
import { getDefaultSearchTimeRange, getSearchTimePickerOptions } from '@/basic-common/util/common'
import {
  fetchKpi,
  fetchPlanVsActual,
  fetchDeviationAnalysis,
  fetchDetailPage,
  searchVardictPycode,
  fetchBladeDeptCargoOwners
} from './api'

const TARGET_KEY = 'railwayPlanCompletionTarget'

function padRange (start, end) {
  return {
    startTime: start + ' 00:00:00',
    endTime: end + ' 23:59:59'
  }
}

function fmtDateStr (d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export default {
  name: 'PlanCompletionAnalysis',
  components: { NxReportHeader },
  data () {
    return {
      timePreset: 'month',
      dateRange: getDefaultSearchTimeRange('daterange'),
      pickerOptions: getSearchTimePickerOptions('daterange'),
      query: {
        planStatus: '',
        custNo: null,
        cargoDictId: null
      },
      tableKeyword: '',
      sumType: '1',
      targetRate: 95,
      settingsVisible: false,
      kpi: {},
      deviationItems: [],
      cargoOwnerList: [],
      cargoOwnerLoading: false,
      cargoOwnerCache: null,
      cargoList: [],
      cargoLoading: false,
      selectedCargo: null,
      tableRecords: [],
      tableTotal: 0,
      tableLoading: false,
      pagination: { page: 1, size: 10 },
      dualChart: null,
      trendChart: null,
      pieChart: null
    }
  },
  computed: {
    deviationTop () {
      const list = this.deviationItems || []
      return list.slice(0, 6)
    }
  },
  mounted () {
    const saved = localStorage.getItem(TARGET_KEY)
    if (saved != null && saved !== '') {
      const n = parseInt(saved, 10)
      if (!isNaN(n)) this.targetRate = n
    }
    this.initPresetRange()
    this.$nextTick(() => {
      this.dualChart = echarts.init(this.$refs.dualChart)
      this.trendChart = echarts.init(this.$refs.trendChart)
      this.pieChart = echarts.init(this.$refs.pieChart)
      window.addEventListener('resize', this.onResize)
      this.applyFilter()
    })
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.onResize)
    this.dualChart && this.dualChart.dispose()
    this.trendChart && this.trendChart.dispose()
    this.pieChart && this.pieChart.dispose()
  },
  methods: {
    initPresetRange () {
      const now = new Date()
      if (this.timePreset === 'month') {
        const start = new Date(now.getFullYear(), now.getMonth(), 1)
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
        this.dateRange = [fmtDateStr(start), fmtDateStr(end)]
      } else if (this.timePreset === 'lastMonth') {
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        const end = new Date(now.getFullYear(), now.getMonth(), 0)
        this.dateRange = [fmtDateStr(start), fmtDateStr(end)]
      } else if (this.timePreset === 'd30') {
        const end = new Date()
        const start = new Date()
        start.setDate(end.getDate() - 29)
        this.dateRange = [fmtDateStr(start), fmtDateStr(end)]
      }
    },
    onPresetChange () {
      if (this.timePreset !== 'custom') {
        this.initPresetRange()
      }
    },
    buildPayload () {
      if (!this.dateRange || this.dateRange.length !== 2) {
        this.$message.warning('请选择时间范围')
        return null
      }
      const t = padRange(this.dateRange[0], this.dateRange[1])
      return {
        startTime: t.startTime,
        endTime: t.endTime,
        sumType: this.sumType,
        planStatus: this.query.planStatus || undefined,
        keyword: this.tableKeyword || undefined,
        cargoNo: this.selectedCargo && this.selectedCargo.varNo ? this.selectedCargo.varNo : undefined,
        custNo:
          this.query.custNo != null && this.query.custNo !== ''
            ? String(this.query.custNo)
            : undefined,
        targetRate: this.targetRate
      }
    },
    applyFilter () {
      this.pagination.page = 1
      this.loadAll()
    },
    reloadTable () {
      this.pagination.page = 1
      this.loadTable()
    },
    async loadAll () {
      const base = this.buildPayload()
      if (!base) return
      await Promise.all([
        this.loadKpi(base),
        this.loadCharts(),
        this.loadDeviation(base),
        this.loadTable()
      ])
    },
    async loadKpi (base) {
      const b = base || this.buildPayload()
      if (!b) return
      try {
        const { data } = await fetchKpi(b)
        this.kpi = data.data || {}
      } catch (e) {
        this.kpi = {}
      }
    },
    async loadCharts () {
      const base = this.buildPayload()
      if (!base || !this.dualChart || !this.trendChart) return
      try {
        const { data } = await fetchPlanVsActual({ ...base, sumType: this.sumType })
        const d = data.data || {}
        const titles = d.title || []
        const planW = (d.planWeight || []).map((v) => Number(v) || 0)
        const actualW = (d.actualWeight || []).map((v) => Number(v) || 0)
        const rates = (d.achievementRate || []).map((v) => (v != null ? Number(v) : null))

        this.dualChart.setOption(
          {
            tooltip: { trigger: 'axis' },
            legend: { data: ['计划量(吨)', '实际完成量(吨)', '达成率(%)'] },
            grid: { left: '3%', right: '4%', bottom: '3%', top: '18%', containLabel: true },
            xAxis: { type: 'category', data: titles },
            yAxis: [
              { type: 'value', name: '吨', splitLine: { lineStyle: { type: 'dashed' } } },
              { type: 'value', name: '%', min: 0, max: 120, splitLine: { show: false } }
            ],
            series: [
              {
                name: '计划量(吨)',
                type: 'bar',
                data: planW,
                itemStyle: { color: '#409EFF' },
                barMaxWidth: 28
              },
              {
                name: '实际完成量(吨)',
                type: 'bar',
                data: actualW,
                itemStyle: { color: '#67C23A' },
                barMaxWidth: 28
              },
              {
                name: '达成率(%)',
                type: 'line',
                yAxisIndex: 1,
                data: rates,
                smooth: true,
                symbol: 'circle',
                symbolSize: 8,
                itemStyle: { color: '#E6A23C' }
              }
            ]
          },
          true
        )

        this.trendChart.setOption(
          {
            tooltip: { trigger: 'axis' },
            grid: { left: '3%', right: '4%', bottom: '3%', top: '12%', containLabel: true },
            xAxis: { type: 'category', data: titles, boundaryGap: false },
            yAxis: { type: 'value', name: '%', min: 0, max: 120 },
            series: [
              {
                name: '达成率',
                type: 'line',
                smooth: true,
                areaStyle: { color: 'rgba(64,158,255,0.08)' },
                data: rates,
                markLine: {
                  symbol: 'none',
                  data: [{ yAxis: this.targetRate, name: '目标' }],
                  lineStyle: { type: 'dashed', color: '#F56C6C' },
                  label: { formatter: '目标 {c}%' }
                }
              }
            ]
          },
          true
        )
      } catch (e) {
        this.$message.error('图表加载失败')
      }
    },
    async loadDeviation (base) {
      const b = base || this.buildPayload()
      if (!b || !this.pieChart) return
      try {
        const { data } = await fetchDeviationAnalysis(b)
        const wrap = data.data || {}
        this.deviationItems = wrap.items || []
        const items = this.deviationItems
        this.pieChart.setOption(
          {
            tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
            legend: { bottom: 0, type: 'scroll' },
            series: [
              {
                type: 'pie',
                radius: ['40%', '65%'],
                center: ['50%', '45%'],
                data: items.map((it) => ({
                  name: it.reasonName,
                  value: it.count != null ? it.count : 0
                })),
                emphasis: { itemStyle: { shadowBlur: 10 } }
              }
            ]
          },
          true
        )
      } catch (e) {
        this.deviationItems = []
      }
    },
    async loadTable () {
      const base = this.buildPayload()
      if (!base) return
      this.tableLoading = true
      try {
        const { data } = await fetchDetailPage({
          ...base,
          page: this.pagination.page,
          size: this.pagination.size
        })
        const page = data.data || {}
        this.tableRecords = page.records || []
        this.tableTotal = page.total || 0
      } catch (e) {
        this.tableRecords = []
        this.tableTotal = 0
      } finally {
        this.tableLoading = false
      }
    },
    saveSettings () {
      localStorage.setItem(TARGET_KEY, String(this.targetRate))
      this.settingsVisible = false
      this.loadAll()
    },
    onResize () {
      this.dualChart && this.dualChart.resize()
      this.trendChart && this.trendChart.resize()
      this.pieChart && this.pieChart.resize()
    },
    fmtPercent (v) {
      if (v === null || v === undefined || v === '') return '-'
      return `${Number(v).toFixed(1)}%`
    },
    fmtSignedPercent (v) {
      if (v === null || v === undefined || v === '') return '-'
      const n = Number(v)
      return (n > 0 ? '+' : '') + `${n.toFixed(1)}%`
    },
    momText (delta) {
      const n = Number(delta)
      if (Math.abs(n) < 0.05) return '持平'
      return (n > 0 ? '增加 ' : '减少 ') + `${Math.abs(n).toFixed(1)} 个百分点`
    },
    progressPct (v) {
      if (v == null || v === '') return 0
      const n = Number(v)
      if (n > 100) return 100
      if (n < 0) return 0
      return Math.round(n * 10) / 10
    },
    rateColor (v) {
      if (v == null) return '#909399'
      const n = Number(v)
      if (n >= 95) return '#67C23A'
      if (n >= 80) return '#E6A23C'
      return '#F56C6C'
    },
    rateTagType (v) {
      if (v == null) return 'info'
      const n = Number(v)
      if (n >= 95) return 'success'
      if (n >= 80) return 'warning'
      return 'danger'
    },
    statusTag (code) {
      if (code === '01') return 'success'
      if (code === '03') return 'danger'
      return 'warning'
    },
    fmtTon (row, col, val) {
      if (val === null || val === undefined || val === '') return '-'
      return Number(val).toFixed(2)
    },
    fmtTonCell (val) {
      if (val === null || val === undefined || val === '') return '-'
      return Number(val).toFixed(2)
    },
    deviationClass (val) {
      if (val == null) return ''
      return Number(val) < 0 ? 'txt-neg' : 'txt-pos'
    },
    sameId (a, b) {
      return a != null && b != null && String(a) === String(b)
    },
    cargoLabel (row) {
      if (!row) return ''
      return row.cargoName || row.varName || ''
    },
    onCargoChange (val) {
      if (val == null || val === '') {
        this.selectedCargo = null
        return
      }
      const row = this.cargoList.find((c) => this.sameId(c.id, val))
      if (row) {
        this.selectedCargo = { id: row.id, varNo: row.varNo, varName: row.varName, cargoName: row.cargoName }
      }
    },
    async ensureCargoOwnerCache () {
      if (this.cargoOwnerCache != null) {
        return this.cargoOwnerCache
      }
      const { data } = await fetchBladeDeptCargoOwners({})
      const raw = Array.isArray(data) ? data : data && data.data
      const list = Array.isArray(raw) ? raw : []
      this.cargoOwnerCache = list
      return list
    },
    async remoteCustOwner (q) {
      this.cargoOwnerLoading = true
      try {
        const all = await this.ensureCargoOwnerCache()
        const kw = (q || '').trim().toLowerCase()
        let list = all
        if (kw) {
          list = all.filter(function (r) {
            return (r.deptName || '').toLowerCase().indexOf(kw) !== -1
          })
        }
        this.cargoOwnerList = list.slice(0, 200)
      } catch (e) {
        this.cargoOwnerList = []
      } finally {
        this.cargoOwnerLoading = false
      }
    },
    async remoteCargo (q) {
      this.cargoLoading = true
      try {
        const { data } = await searchVardictPycode(q)
        const raw = Array.isArray(data) ? data : data && data.data
        this.cargoList = Array.isArray(raw) ? raw : []
      } catch (e) {
        this.cargoList = []
      } finally {
        this.cargoLoading = false
      }
    },
    async exportReport () {
      const base = this.buildPayload()
      if (!base) return
      const maxRows = 2000
      const size = Math.min(this.tableTotal || maxRows, maxRows)
      if (size < 1) {
        this.$message.warning('无数据可导出')
        return
      }
      try {
        const { data } = await fetchDetailPage({ ...base, page: 1, size })
        const list = (data.data && data.data.records) || []
        const { export_json_to_excel } = require('@/util/vendor/Export2Excel')
        const th = [
          '计划编号',
          '计划日期',
          '计划内容',
          '货主',
          '计划量',
          '实际完成量',
          '达成率%',
          '偏差量',
          '偏差原因',
          '状态'
        ]
        const rows = list.map((r) => [
          r.planLineNo || '',
          r.planDate || '',
          r.planContent || '',
          r.custName || '',
          r.planQuantity != null ? Number(r.planQuantity).toFixed(2) : '',
          r.actualQuantity != null ? Number(r.actualQuantity).toFixed(2) : '',
          r.achievementRate != null ? Number(r.achievementRate).toFixed(2) : '',
          r.deviationQty != null ? Number(r.deviationQty).toFixed(2) : '',
          r.deviationReason || '',
          r.planStatusLabel || ''
        ])
        export_json_to_excel(th, rows, '计划完成率明细')
        if (this.tableTotal > maxRows) {
          this.$message.warning(`导出前 ${maxRows} 条，共 ${this.tableTotal} 条`)
        }
      } catch (e) {
        this.$message.error('导出失败')
      }
    }
  }
}
</script>

<style scoped>
.plan-completion-page {
  padding: 0;
  background: transparent;
  min-height: auto;
}
.page-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}
.page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}
.page-desc {
  margin: 6px 0 0;
  font-size: 13px;
  color: #909399;
}
.head-actions {
  display: flex;
  gap: 8px;
}
.filter-card {
  margin-bottom: 16px;
}
.filter-title {
  font-weight: 600;
  margin-bottom: 12px;
  color: #303133;
}
.filter-form {
  background: transparent;
  padding: 0;
  border-radius: 0;
}
.kpi-row {
  margin-bottom: 16px;
}
.kpi-card {
  position: relative;
  text-align: center;
  padding: 16px 8px 12px;
  min-height: 120px;
}
.kpi-icon {
  position: absolute;
  top: 12px;
  right: 12px;
  font-size: 22px;
  opacity: 0.35;
}
.kpi-val {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.2;
}
.kpi-label {
  font-size: 14px;
  color: #606266;
  margin-top: 6px;
}
.kpi-sub {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}
.kpi-blue .kpi-val {
  color: #409eff;
}
.kpi-red .kpi-val {
  color: #f56c6c;
}
.kpi-gold .kpi-val {
  color: #e6a23c;
}
.kpi-green .kpi-val {
  color: #67c23a;
}
.block-card {
  margin-bottom: 16px;
}
.card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.card-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}
.target-hint {
  font-size: 13px;
  color: #606266;
}
.echart-box {
  width: 100%;
  height: 320px;
}
.echart-box.tall {
  height: 360px;
}
.pie-box {
  height: 340px;
}
.bottom-row {
  margin-bottom: 16px;
}
.reason-foot {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 10px;
  margin-top: 8px;
}
.reason-item {
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
  border-left: 4px solid #409eff;
  background: #fafafa;
}
.reason-item.c1 {
  border-left-color: #f56c6c;
}
.reason-item.c2 {
  border-left-color: #409eff;
}
.reason-item.c3 {
  border-left-color: #e6a23c;
}
.reason-item.c0 {
  border-left-color: #67c23a;
}
.reason-name {
  font-weight: 600;
  color: #303133;
}
.reason-meta {
  color: #909399;
  margin-top: 4px;
}
.mom {
  color: #606266;
}
.trend-up {
  color: #67c23a;
}
.trend-down {
  color: #f56c6c;
}
.trend-flat {
  color: #909399;
}
.empty-hint {
  text-align: center;
  color: #909399;
  padding: 24px;
}
.table-card {
  background: #fff;
}
.pager {
  margin-top: 12px;
  text-align: right;
}
.txt-neg {
  color: #f56c6c;
}
.txt-pos {
  color: #67c23a;
}
</style>
